import { useState } from "react";
import { api } from "../api.js";
import { t } from "../i18n.js";
import { go, isManager, useMe } from "../shared.jsx";
import { Empty, ErrorBox, Field, Loader, Modal, useAction, useLoad } from "../ui.jsx";

export function TeamList() {
  const me = useMe();
  const state = useLoad(() => api.get("/teams?page_size=100"), []);
  const [creating, setCreating] = useState(false);
  return (
    <div className="page">
      <header className="page-head"><h1>{t("teams")}</h1>
        {isManager(me) && <button className="btn btn-primary" onClick={() => setCreating(true)}>فريق جديد</button>}</header>
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<Empty title="لا توجد فرق">أنشئ فريقًا وحدد قائده وأعضاءه.</Empty>}>
        {(d) => (
          <table className="table table-rows">
            <thead><tr><th>الفريق</th><th>التخصص</th><th>القائد</th><th>الأعضاء</th><th>مشاريع جارية</th></tr></thead>
            <tbody>{d.items.map((tm) => (
              <tr key={tm.id} onClick={() => go(`/teams/${tm.id}`)} className={tm.active ? "" : "muted"}>
                <td><a href={`#/teams/${tm.id}`}>{tm.name}</a>{!tm.active && " (غير نشط)"}</td><td>{tm.specialty || "—"}</td>
                <td>{tm.leader_name || "—"}</td><td>{tm.member_count}</td><td>{tm.active_projects}</td></tr>))}</tbody>
          </table>
        )}
      </Loader>
      {creating && <TeamForm onClose={() => setCreating(false)} onSaved={(tm) => go(`/teams/${tm.id}`)} />}
    </div>
  );
}

function TeamForm({ team, onClose, onSaved }) {
  const users = useLoad(() => api.get("/users?status=active&page_size=100"), []);
  const [f, setF] = useState({ name: team?.name || "", specialty: team?.specialty || "", description: team?.description || "",
    leader_id: team?.leader_id || "", member_ids: team ? team.members.map((m) => m.id) : [] });
  const { busy, error, run } = useAction();
  const submit = (e) => {
    e.preventDefault();
    const req = team
      ? api.patch(`/teams/${team.id}`, { version: team.version, name: f.name, specialty: f.specialty || null,
        description: f.description || null, leader_id: f.leader_id || null })
        .then(() => api.put(`/teams/${team.id}/members`, { member_ids: f.member_ids }))
      : api.post("/teams", { ...f, leader_id: f.leader_id || null });
    run(() => req, "تم حفظ الفريق").then(onSaved, () => {});
  };
  const list = users.data?.items || [];
  return (
    <Modal title={team ? "تعديل الفريق" : "فريق جديد"} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="اسم الفريق"><input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="التخصص"><input list="specialties" value={f.specialty} onChange={(e) => setF({ ...f, specialty: e.target.value })} /></Field>
        <datalist id="specialties">{["كاميرات المراقبة", "الشبكات", "التحكم بالدخول", "البوابات الذكية", "الصوتيات"].map((s) => <option key={s} value={s} />)}</datalist>
        <Field label="الوصف"><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="قائد الفريق"><select value={f.leader_id} onChange={(e) => setF({ ...f, leader_id: e.target.value })}>
          <option value="">بدون قائد</option>{list.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></Field>
        <fieldset className="field"><legend className="field-label">الأعضاء</legend>
          <div className="checks checks-scroll">{list.map((u) => (
            <label className="check" key={u.id}><input type="checkbox" checked={f.member_ids.includes(u.id)}
              onChange={(e) => setF({ ...f, member_ids: e.target.checked ? [...f.member_ids, u.id] : f.member_ids.filter((x) => x !== u.id) })} />
              {u.name}{u.job_title ? ` — ${u.job_title}` : ""}</label>))}</div>
        </fieldset>
        <ErrorBox error={error} />
        <div className="actions"><button className="btn btn-primary" disabled={busy}>{busy ? "جارٍ الحفظ…" : "حفظ"}</button>
          <button type="button" className="btn" onClick={onClose}>{t("cancel")}</button></div>
      </form>
    </Modal>
  );
}

export function TeamDetail({ id }) {
  const me = useMe();
  const state = useLoad(() => api.get(`/teams/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const { run } = useAction();
  return (
    <div className="page">
      <a className="back" href="#/teams"> → الفرق</a>
      <Loader state={state}>{(tm) => (
        <>
          <header className="page-head"><div><h1>{tm.name}</h1><p className="muted">{tm.specialty}</p></div>
            {isManager(me) && <div className="actions">
              <button className="btn" onClick={() => setEditing(true)}>تعديل</button>
              <button className="btn" onClick={() => run(() => api.patch(`/teams/${id}`, { version: tm.version, active: !tm.active }), tm.active ? "عُطّل الفريق" : "فُعّل الفريق").then(state.reload, () => {})}>
                {tm.active ? "تعطيل الفريق" : "تفعيل الفريق"}</button></div>}</header>
          {tm.description && <p className="prose">{tm.description}</p>}
          <div className="grid-2">
            <section className="panel"><h2>الأعضاء<span className="count">{tm.members.length}</span></h2>
              <ul className="plain">{tm.members.map((m) => <li key={m.id}>{m.name}{m.id === tm.leader_id ? " — قائد الفريق" : ""}
                <small className="muted"> {m.job_title || ""} {m.status === "disabled" ? "(معطّل)" : ""}</small></li>)}</ul></section>
            <section className="panel"><h2>المشاريع<span className="count">{tm.projects.length}</span></h2>
              <ul className="plain">{tm.projects.map((p) => <li key={p.id}><a href={`#/projects/${p.id}`}>{p.ref_code} — {p.name}</a>
                <small className="muted"> ({t("projectStatus." + p.status)})</small></li>)}</ul></section>
          </div>
          {editing && <TeamForm team={tm} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); state.reload(); }} />}
        </>
      )}</Loader>
    </div>
  );
}
